// Shared client-side readers for the payment API's structured error/outcome
// contracts. Every client surface that calls /api/payments/{cancel,retry},
// the request-withdraw endpoint or starts a checkout must speak the same
// language about money — these helpers are the single place that knows the
// wire shapes.
//
// ── PAYMENT_IN_REVIEW (409 { code, error }) ────────────────────────────────
// The gateway is still holding the client's money (authorized / under review /
// captured) and the void could NOT be verified, so the server refuses to tear
// the checkout down. This is NOT a failure the client caused and NOT an error
// state: their payment probably went through. It renders as a calm, PERSISTENT
// informational state — never a red error, never a toast that disappears.
//
// ── PENDING_DRIFT (400 { code, message, breakdown }) ───────────────────────
// The outstanding pending-invoice total moved between the figure the client
// was shown and the figure the server would charge. Note the payload carries
// `message` (NOT `error`) — a caller that only reads data.error shows a
// useless generic string and, on the retry rails, dead-ends forever because
// the next retry re-compares against the same stale baseline. The recovery is
// to SHOW the fresh figure and send it back as expected_total on the next
// attempt, which the server accepts as the acknowledged amount.

const money = (n) =>
    `AED ${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const PAYMENT_IN_REVIEW_FALLBACK =
    // TRUTHFULNESS (2026-07-23): PAYMENT_IN_REVIEW means the gateway would not
    // let us verifiably tear the checkout down — we do NOT know the money moved.
    // The old copy asserted it "was approved", so a client who simply closed the
    // paypage was told their payment had gone through.
    'We’re still confirming this card payment with the gateway. It can’t be cancelled while the result is pending — please don’t pay again yet; we’ll update your account automatically as soon as the bank confirms.';

// 409 + code PAYMENT_IN_REVIEW — money is in flight at the gateway.
export const isPaymentInReview = (err) =>
    err?.response?.status === 409 && err?.response?.data?.code === 'PAYMENT_IN_REVIEW';

// The server's own truthful wording (it explains what is happening) with a
// calm fallback if the body is missing.
export const paymentInReviewMessage = (err) =>
    err?.response?.data?.error || PAYMENT_IN_REVIEW_FALLBACK;

// 400 + code PENDING_DRIFT — the amount due changed.
export const isPendingDrift = (err) => err?.response?.data?.code === 'PENDING_DRIFT';

// The acknowledged figure to send back as expected_total on the next attempt.
// capture_total is what the gateway would actually capture (pending + top-up /
// card portion); `total` is the plain pending-only shape.
export const driftCaptureTotal = (err) => {
    const b = err?.response?.data?.breakdown || {};
    const n = Number(b.capture_total ?? b.total);
    return Number.isFinite(n) ? n : null;
};

// Client-facing drift copy: quote the fresh figure and say plainly that tapping
// again pays THAT amount, so the next attempt is a conscious acknowledgement.
export const pendingDriftMessage = (err) => {
    const fresh = driftCaptureTotal(err);
    if (fresh != null) {
        return `The amount due changed to ${money(fresh)} — tap again to pay the updated amount.`;
    }
    return err?.response?.data?.message
        || 'The amount due has changed — please review the fresh figure before paying.';
};

// ── SCHEMA_NOT_READY (503 { code, error, missing }) ───────────────────────
// routes/services/schemaPreflight.js fail-closes every money path BEFORE the
// gateway is touched when a migration lags on the server. Its `error` string is
// written for an OPERATOR and names migration files:
//
//   "This payment cannot be processed: the database is missing a required
//    migration (2026_07_payments_9.sql, …). No money has been taken. Run the
//    pending migrations and retry — see DEPLOY_DB_MIGRATIONS.md."
//
// A paying client must NEVER see a filename, a table name or an instruction to
// run SQL. Clients get calm copy that keeps the one guarantee that matters:
// nothing was charged. The operator text still reaches staff surfaces and logs.
//
// Matched on the explicit CODE only. A bare `status === 503` (proxy, load
// balancer, cold restart) is NOT schema-not-ready: on a cancel/retry/verify
// path it means "we could not reach the server", which is AMBIGUOUS about the
// client's money and must never render "nothing was charged".
export const isSchemaNotReady = (err) =>
    err?.response?.data?.code === 'SCHEMA_NOT_READY' || err?.data?.code === 'SCHEMA_NOT_READY'
    || err?.code === 'SCHEMA_NOT_READY';

export const SCHEMA_NOT_READY_TITLE = 'Payments are temporarily unavailable';

export const SCHEMA_NOT_READY_MESSAGE =
    'Card payments are temporarily unavailable while we finish a scheduled update. '
    + 'Nothing was charged and nothing was submitted. Please try again shortly — '
    + 'your details are kept.';

// ── Structured codes that need CLIENT copy, not the server's string ────────
// Some backend codes carry text written for an operator or for a different
// client (the mobile app). A paying client must never meet "update your app",
// a version number, or a migration name. Anything listed here is rewritten;
// anything not listed still falls through to the server's own wording, which
// for the ordinary money errors is the truthful, client-facing text.
//
// APP_UPDATE_REQUIRED: the server is refusing a payload this client build
// cannot produce correctly. On MOBILE that means "install the update"; on WEB
// the equivalent is a hard reload, because the browser is holding a stale
// bundle. The one guarantee that matters in both cases — nothing was charged —
// leads the sentence.
export const CODE_MESSAGES = {
    APP_UPDATE_REQUIRED:
        'Nothing was charged. This page is running an older version of the site — '
        + 'please refresh (or close and reopen this tab) and try again.',
    PAYMENT_METHOD_REQUIRED:
        'Please choose how you’d like to pay — wallet, card, or both — before confirming.',
    SETTLEMENT_IN_REVIEW:
        'Your card payment is being reviewed by the bank. Nothing more is needed from you, '
        + 'and you should not pay again — we’ll update your account as soon as it clears.',
    SETTLEMENT_AMOUNT_FROZEN:
        'This payment is already being processed, so the amount can’t change now. '
        + 'Please wait for it to finish before making another change.',
    ALREADY_PROCESSING:
        'This is already being processed — please give it a moment rather than trying again, '
        + 'so you are not charged twice.',
    MIT_DISABLED:
        'Automatic card payments are switched off at the moment. You can still pay from your '
        + 'wallet or by card.',
    NO_SAVED_CARD:
        'There is no saved card on your account for this. Please pay by card and choose to '
        + 'save it for next time.',
    PENDING_LOOKUP_FAILED:
        'We couldn’t check your outstanding invoices just now, so we haven’t taken anything. '
        + 'Please try again in a moment.',
};

export const codeMessage = (err) => {
    const code = err?.response?.data?.code || err?.data?.code || err?.code;
    return (typeof code === 'string' && CODE_MESSAGES[code]) || null;
};

// Generic message reader that respects BOTH shapes: `error` (most routes) and
// `message` (the drift guards), swaps the operator-facing SCHEMA_NOT_READY text
// for client copy, and rewrites any code in CODE_MESSAGES. Callers pass their
// own final fallback.
export const paymentErrorMessage = (err, fallback) => {
    if (isSchemaNotReady(err)) return SCHEMA_NOT_READY_MESSAGE;
    const mapped = codeMessage(err);
    if (mapped) return mapped;
    return err?.response?.data?.error || err?.response?.data?.message || fallback;
};

// ── /payments/cancel outcome contract (W6) ────────────────────────────────
// POST /api/payments/cancel answers 200 with the checkout's RESULTING outcome.
// Every caller used to treat "not 'paid'" as "cancelled, nothing was charged".
// That fails OPEN: a renamed/dropped `outcome` field, a proxy-injected 409, an
// unseen future status — all silently render a permanent lie about money over a
// real capture. Money UI must fail CLOSED: only an EXPLICIT not-paid outcome may
// claim nothing was charged; everything else is "still confirming, do not pay
// again". Never widen NOT_PAID_OUTCOMES to make an unknown value comfortable.

// The closed set the server is contracted to return.
export const CANCEL_OUTCOMES = ['paid', 'cancelled', 'failed', 'expired', 'pending'];

// Outcomes that positively prove the client's money was NOT taken.
export const NOT_PAID_OUTCOMES = ['cancelled', 'failed', 'expired'];

const readOutcome = (v) => {
    // Accept either an axios response or a raw body; normalise case/whitespace so
    // 'Cancelled' / ' CANCELLED ' can never fall through to the ambiguous branch.
    const body = v?.data && typeof v.data === 'object' ? v.data : v;
    const o = body?.outcome;
    return typeof o === 'string' ? o.trim().toLowerCase() : null;
};

// TRUE only when the body explicitly says the money was not taken.
export const isDefinitelyNotPaid = (res) => NOT_PAID_OUTCOMES.includes(readOutcome(res));

// TRUE only when the body explicitly says the money WAS taken.
export const isDefinitelyPaid = (res) => readOutcome(res) === 'paid';

// Everything else — missing field, renamed field, 'pending', an unknown string.
// The client must be told we are still confirming, and must NOT be invited to
// pay again.
export const isAmbiguousCancelResult = (res) =>
    !isDefinitelyPaid(res) && !isDefinitelyNotPaid(res);

// A 409 may only render the cancelled confirmation when the BODY says so. A bare
// `status === 409` (proxy, future backend code, gateway shim) is ambiguous.
export const isConfirmedCancelledError = (err) => {
    const data = err?.response?.data;
    if (data?.code === 'ALREADY_CANCELLED') return true;
    return isDefinitelyNotPaid(data);
};

export const CANCEL_UNCONFIRMED_MESSAGE =
    'We couldn’t confirm the result of this payment yet. Please do NOT pay again — '
    + 'if any money was taken it will be credited automatically, and we’ll update '
    + 'your account as soon as the bank confirms. Refresh in a few minutes to check.';

// ── The AWAITING-PAYMENT note (W3, same root cause as the cancel readers) ──
// An unfinished checkout is `gateway_checkouts.status === 'created'` — which is
// exactly the state routes/payments.js treats as possibly holding AUTHORISED /
// CAPTURED / POST_AUTH_REFER money (the `money_in_flight` branch). So 'created'
// is NOT evidence the client's money stayed put, and any banner gated on it may
// never assert "nothing has been charged". Only an EXPLICIT not-paid cancel
// outcome proves that, and that proof comes from readCancelSettlement — not
// from the list row.
//
// Exact mirror of the mobile app's AWAITING_PAYMENT_NOTE so both clients say
// the same thing about the same money.
export const AWAITING_PAYMENT_NOTE =
    'This card payment was never completed, so your wallet hasn’t been credited. '
    + 'Resume it to finish, or cancel it — if the bank did take anything for this '
    + 'attempt, we credit it to your wallet automatically. Please don’t pay twice.';

export { money as formatAed };

// ── payment_state on a client_requests row (server-classified) ─────────────
// GET /api/client-requests/requests/my classifies each row's linked checkout:
//   'awaiting_payment'  → checkout still 'created' AND the gateway has NOT
//                         reported any money state. The paypage may be resumed.
//   'payment_in_review' → the gateway holds an authorised/held payment
//                         (POST_AUTH_REFER / REVIEW / AUTHORISED). The money
//                         very likely MOVED. The stored payment_url points at a
//                         CONSUMED paypage session, so the server nulls it — a
//                         Pay button here is a dead button and a lie about money.
//   null                → nothing outstanding.
// Older servers (pre-classifier) only ever send 'awaiting_payment'; these
// readers degrade to exactly today's behaviour in that case.
export const AWAITING_PAYMENT_STATE = 'awaiting_payment';
export const PAYMENT_IN_REVIEW_STATE = 'payment_in_review';

export const isReviewHeldRow = (row) => row?.payment_state === PAYMENT_IN_REVIEW_STATE;
export const isAwaitingPaymentRow = (row) => row?.payment_state === AWAITING_PAYMENT_STATE;

// Calm, standing copy for a review-held request. Never a red error: the client
// did nothing wrong and there is nothing for them to do.
export const REVIEW_HELD_NOTE =
    'Your card payment is being reviewed by the bank. We’ll confirm this booking automatically '
    + 'as soon as the review finishes — there is nothing more for you to do, and you should not '
    + 'pay again. If the bank hasn’t confirmed within an hour we release the payment and cancel '
    + 'this request for you automatically.';

// Why the Withdraw / Cancel control is not offered on a review-held request.
export const REVIEW_HELD_WITHDRAW_NOTE =
    'This request can’t be withdrawn while the bank is still holding the payment — we’d have no '
    + 'way to tell you truthfully whether you were charged.';

// The one thing a client most needs to know when a request is stuck in review.
export const REVIEW_HELD_OTHER_BOOKINGS_NOTE =
    'This only affects this request — you can still make other bookings as normal.';

// ── The single decision point every WEB cancel site branches on ───────────
// Exact mirror of the mobile app's readCancelSettlement /
// readCancelErrorSettlement (HHCMobileApp/hooks/clientHooks/paymentErrors.ts).
// PaymentResultOverlay, ClientWallet, BookingRequestDetails and
// ClientMyRequests all route through these, so there is ONE definition of
// "did the client's money move" on web and it matches mobile's. Hand-rolling
// the branch at a call site is what caused the three earlier bugs — don't.
//
// 'cancelled'   → explicitly not paid; it is safe to say nothing was charged.
// 'paid'        → the money DID move; never imply otherwise.
// 'review'      → the gateway is still holding it (PAYMENT_IN_REVIEW).
// 'unconfirmed' → everything else. Persistent "still confirming", keep
//                 verifying, and never "nothing was charged".

// What to tell the client after a 2xx /payments/cancel (or /verify).
export const readCancelSettlement = (res) => {
    if (isDefinitelyPaid(res)) return 'paid';
    if (isDefinitelyNotPaid(res)) return 'cancelled';
    return 'unconfirmed';
};

// The same decision for a THROWN cancel (409 / 5xx / network). Note a bare 503
// or a bare 409 lands in 'unconfirmed' — never 'cancelled'.
export const readCancelErrorSettlement = (err) => {
    if (isPaymentInReview(err)) return 'review';
    if (isDefinitelyPaid(err?.response?.data)) return 'paid';
    if (isConfirmedCancelledError(err)) return 'cancelled';
    return 'unconfirmed';
};
